import React from 'react';
import { haptics, usesSwitchHaptics, areHapticsEnabled } from '../../utils/haptics';

export type HapticName =
  | 'tap'
  | 'softTap'
  | 'notice'
  | 'boundary'
  | 'transition'
  | 'success'
  | 'rowNext'
  | 'rowPrev'
  | 'sessionPause';

export interface TouchableProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick'
> {
  haptic: HapticName | null;
  hapticOverride?: HapticName;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  ref?: React.Ref<HTMLButtonElement>;
}

// Keyboard/switch-control users need a visible focus indicator. Applied here on
// the base so every button variant gets it consistently (previously only the
// `lg` Primary/Soft variants had a ring). `focus-visible` keeps it keyboard-only
// so mouse/touch presses don't show a ring.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';

export const Touchable: React.FC<TouchableProps> = ({
  haptic,
  hapticOverride,
  onClick,
  type,
  disabled,
  children,
  ref,
  className,
  ...rest
}) => {
  const ariaDisabled = rest['aria-disabled'];
  const isAriaDisabled = ariaDisabled === true || ariaDisabled === 'true';
  const resolvedHaptic = hapticOverride ?? haptic;

  // iOS Safari can't fire a taptic from navigator.vibrate or a programmatic
  // click — only from a real user toggle of a real <input switch>. So on that
  // platform we render haptic buttons as a <label> wrapping a hidden switch:
  // the user's tap toggles it and iOS plays the taptic. The switch is disabled
  // when haptics are off, so it can't buzz, while the label click still runs
  // the action. Everywhere else keeps a plain <button> (Android buzzes via
  // navigator.vibrate in handleClick; desktop no-ops). This branch is inert
  // outside iOS Safari, so it changes nothing for other platforms or tests.
  if (resolvedHaptic && usesSwitchHaptics()) {
    const activate = (e: React.SyntheticEvent) => {
      if (disabled || isAriaDisabled) return;
      // No haptics.* call here: the taptic comes from the physical toggle.
      onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
    };
    // A <label> can't match the `:disabled` pseudo-class, so disabled: utilities
    // (e.g. PrimaryButton's `disabled:opacity-50`) don't apply. Re-create the
    // dimmed affordance here so disabled buttons still look disabled on iOS.
    const disabledClasses = disabled ? ' opacity-50 cursor-not-allowed' : '';
    const base = `ktt-switch-btn ${FOCUS_RING} relative${disabledClasses}`;
    return (
      <label
        ref={ref as unknown as React.Ref<HTMLLabelElement>}
        role="button"
        aria-disabled={disabled || isAriaDisabled ? true : undefined}
        tabIndex={disabled ? undefined : 0}
        onClick={activate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate(e);
          }
        }}
        className={className ? `${base} ${className}` : base}
        {...(rest as unknown as React.LabelHTMLAttributes<HTMLLabelElement>)}
      >
        <input
          type="checkbox"
          aria-hidden="true"
          tabIndex={-1}
          disabled={disabled || isAriaDisabled || !areHapticsEnabled()}
          ref={(el) => {
            if (el) el.setAttribute('switch', '');
          }}
          // Stop the label-forwarded click from bubbling back and double-firing
          // the action; the toggle (and its taptic) still happen.
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: 1,
            height: 1,
            margin: 0,
          }}
        />
        {children}
      </label>
    );
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !isAriaDisabled) {
      if (resolvedHaptic) haptics[resolvedHaptic]();
    }
    onClick?.(e);
  };

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled}
      onClick={handleClick}
      className={className ? `${FOCUS_RING} ${className}` : FOCUS_RING}
      {...rest}
    >
      {children}
    </button>
  );
};
